import { motion, useReducedMotion } from "motion/react";
import { Heart, Sparkle, Star } from "lucide-react";
import type { CSSProperties } from "react";

/**
 * Décor animé derrière la grille des tarifs : blobs flous teintés
 * (sage/terra/honey/blush) en pulsation lente + quelques formes douces
 * flottantes. Purement décoratif (aria-hidden). Les boucles de transform
 * sont neutralisées par MotionConfig reducedMotion="user" ; les formes
 * flottantes sont entièrement coupées si l'utilisateur réduit le mouvement.
 */

type Blob = {
  className: string;
  size: string;
  style: CSSProperties;
  anim: { scale: number[]; x: number[]; y: number[] };
  dur: number;
  delay: number;
};

const BLOBS: Blob[] = [
  {
    className: "bg-sage/25",
    size: "w-64 h-64",
    style: { top: "1.5rem", left: "1.5rem" },
    anim: { scale: [1, 1.1, 1], x: [0, 14, 0], y: [0, -10, 0] },
    dur: 15,
    delay: 0,
  },
  {
    className: "bg-terra/25",
    size: "w-72 h-72",
    style: { top: "20%", right: "1.5rem" },
    anim: { scale: [1, 1.12, 1], x: [0, -16, 0], y: [0, 12, 0] },
    dur: 17,
    delay: 1.4,
  },
  {
    className: "bg-honey/30",
    size: "w-56 h-56",
    style: { bottom: "1.5rem", left: "18%" },
    anim: { scale: [1, 1.08, 1], x: [0, 12, 0], y: [0, 10, 0] },
    dur: 16,
    delay: 0.7,
  },
  {
    className: "bg-blush/25",
    size: "w-48 h-48",
    style: { bottom: "8%", right: "14%" },
    anim: { scale: [1, 1.1, 1], x: [0, -12, 0], y: [0, -10, 0] },
    dur: 18,
    delay: 2.1,
  },
];

type Shape = {
  Icon: typeof Heart;
  className: string;
  style: CSSProperties;
  size: number;
  drift: number;
  duration: number;
  delay: number;
  opacity: number;
};

const SHAPES: Shape[] = [
  { Icon: Heart, className: "text-blush", style: { left: "8%", top: "22%" }, size: 20, drift: 12, duration: 12, delay: 0, opacity: 0.5 },
  { Icon: Sparkle, className: "text-honey-hover", style: { right: "11%", top: "18%" }, size: 18, drift: -12, duration: 13, delay: 1.6, opacity: 0.55 },
  { Icon: Star, className: "text-sage", style: { left: "16%", bottom: "18%" }, size: 16, drift: 10, duration: 14, delay: 0.8, opacity: 0.45 },
  { Icon: Sparkle, className: "text-terra", style: { right: "18%", bottom: "22%" }, size: 14, drift: -10, duration: 15, delay: 2.4, opacity: 0.5 },
];

export default function TarifsDecor() {
  const reduce = useReducedMotion();

  return (
    <div
      className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-3xl"
      aria-hidden="true"
    >
      {BLOBS.map((b, i) => (
        <motion.div
          key={i}
          className={`absolute rounded-full blur-2xl ${b.size} ${b.className}`}
          style={b.style}
          animate={{ scale: b.anim.scale, x: b.anim.x, y: b.anim.y }}
          transition={{
            duration: b.dur,
            delay: b.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}

      {!reduce &&
        SHAPES.map((s, i) => (
          <motion.span
            key={`s-${i}`}
            className={`absolute ${s.className}`}
            style={s.style}
            initial={{ opacity: 0, y: 14 }}
            animate={{
              y: [14, -18, 14],
              x: [0, s.drift, 0],
              rotate: [0, s.drift > 0 ? 12 : -12, 0],
              opacity: [0, s.opacity, s.opacity, 0],
            }}
            transition={{
              duration: s.duration,
              delay: s.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <s.Icon size={s.size} strokeWidth={1.6} fill="currentColor" />
          </motion.span>
        ))}
    </div>
  );
}
